import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useReportByCategory, type CategoryBucket } from "../lib/receipts";
import { GRUPPEN_REIHENFOLGE } from "../../lib/gruppen.ts";
import { formatCurrency } from "../lib/format";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { SignedAmount } from "../components/SignedAmount";

const PALETTE = ["#0A84FF", "#30D158", "#FF9F0A", "#FF375F", "#BF5AF2", "#64D2FF", "#FFD60A", "#5E5CE6", "#FF6482", "#AC8E68"];

type Preset = "week" | "month" | "quarter" | "year" | "free";
const PRESET_LABEL: Record<Preset, string> = { week: "Woche", month: "Monat", quarter: "Quartal", year: "Jahr", free: "Frei" };

const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// offset verschiebt die Periode relativ zu heute: 0 = aktuell, −1 = vorige, −2 = vorvorige …
function presetRange(p: Preset, offset = 0, now = new Date()): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (p === "week") {
    const day = (now.getDay() + 6) % 7; // Mo=0
    const mon = new Date(y, m, now.getDate() - day + offset * 7);
    const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
    return { from: fmt(mon), to: fmt(sun) };
  }
  if (p === "month") return { from: fmt(new Date(y, m + offset, 1)), to: fmt(new Date(y, m + offset + 1, 0)) };
  if (p === "quarter") {
    const q = Math.floor(m / 3);
    return { from: fmt(new Date(y, (q + offset) * 3, 1)), to: fmt(new Date(y, (q + offset) * 3 + 3, 0)) };
  }
  return { from: fmt(new Date(y + offset, 0, 1)), to: fmt(new Date(y + offset, 11, 31)) }; // year
}

const MONTHS_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

// ISO-8601-Kalenderwoche aus einem YYYY-MM-DD-String (Woche mit dem ersten Donnerstag = KW1).
function isoWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  const dayNum = (date.getUTCDay() + 6) % 7; // Mo=0
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // Donnerstag dieser Woche
  const firstThu = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstThuNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstThuNum + 3);
  return 1 + Math.round((date.getTime() - firstThu.getTime()) / 604800000);
}

// Menschenlesbares Label der aktuell gewählten Periode (für die ◀ ▶-Navigation).
function periodLabel(p: Preset, range: { from: string; to: string }): string {
  const [y, m] = range.from.split("-").map(Number) as [number, number, number];
  if (p === "week") return `KW ${isoWeek(range.from)} · ${y}`;
  if (p === "month") return `${MONTHS_DE[m - 1]} ${y}`;
  if (p === "quarter") return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
  return `${y}`; // year
}

function shiftYear(date: string, delta: number): string {
  const [y, m, d] = date.split("-");
  return fmt(new Date(Number(y) + delta, Number(m) - 1, Number(d)));
}

// S3: Gruppe der Buchungsart ersetzt die Verwendungsart als Filter.
const GRUPPEN_FILTER: string[] = ["all", ...GRUPPEN_REIHENFOLGE];

export function AuswertungenPage() {
  // Migration 0014 (2026-06-05 spät): Filter-State lebt in URL-Query, damit Browser-Back
  // aus einer Drill-Liste den vorherigen Filter-Zustand wieder herstellt (User-Bug-Befund).
  const [sp, setSp] = useSearchParams();
  const preset: Preset = (() => {
    const v = sp.get("preset");
    return v === "week" || v === "month" || v === "quarter" || v === "year" || v === "free" ? v : "week";
  })();
  const offset = parseInt(sp.get("offset") ?? "0", 10) || 0;
  const customFrom = sp.get("from") ?? fmt(new Date(new Date().getFullYear(), 0, 1));
  const customTo = sp.get("to") ?? fmt(new Date());
  const gruppe: string = (() => {
    const v = sp.get("gruppe");
    return v && GRUPPEN_FILTER.includes(v) ? v : "all";
  })();
  const direction: "all" | "expense" | "income" = (() => {
    const v = sp.get("direction");
    return v === "expense" || v === "income" ? v : "all";
  })();
  const stability: "all" | "fixed" | "variable" | "none" = (() => {
    const v = sp.get("stability");
    return v === "fixed" || v === "variable" || v === "none" ? v : "all";
  })();
  const vorjahr = sp.get("vorjahr") === "1";

  // Helper: Filter setzen (übernimmt nur die abweichenden Werte in die URL, lässt Default leer).
  // ⚠️ Functional Setter (`prev => …`): wenn mehrere setSp-Calls in einer Render-Pass passieren
  // (z.B. `setPreset(p); setOffset(0);`), würde der zweite den ersten überschreiben, weil beide
  // den alten `sp`-Snapshot lesen. Live-Bug 2026-06-05: Monat/Quartal/Jahr/Frei-Buttons reagierten
  // nicht, weil `setOffset(0)` direkt nach `setPreset(p)` das preset wieder rauslöschte.
  const update = (patch: Record<string, string | null>) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  };
  // ⚠️ Live-Verifiziert 2026-06-05: react-router-dom's `setSearchParams(fn, opts)` aktualisiert
  // `prev` ERST nach dem nächsten Render. Mehrere setSp-Calls im selben Event-Handler haben
  // alle dasselbe `prev` → der letzte überschreibt den Effekt der vorigen. Lösung: alle Patches
  // in EINEM update-Aufruf zusammenfassen.
  const setPreset = (p: Preset) => update({ preset: p === "week" ? null : p, offset: null, from: null, to: null });
  const setOffset = (n: number | ((o: number) => number)) => {
    const v = typeof n === "function" ? n(offset) : n;
    update({ offset: v === 0 ? null : String(v) });
  };
  const setCustomFrom = (v: string) => update({ from: v });
  const setCustomTo = (v: string) => update({ to: v });
  const setGruppe = (v: string) => update({ gruppe: v === "all" ? null : v });
  const setDirection = (v: "all" | "expense" | "income") => update({ direction: v === "all" ? null : v });
  const setStability = (v: "all" | "fixed" | "variable" | "none") => update({ stability: v === "all" ? null : v });
  const setVorjahr = (v: boolean) => update({ vorjahr: v ? "1" : null });

  const range = preset === "free" ? { from: customFrom, to: customTo } : presetRange(preset, offset);
  const prior = { from: shiftYear(range.from, -1), to: shiftYear(range.to, -1) };

  const { data, isLoading } = useReportByCategory({ ...range, gruppe, direction, stability });
  const { data: priorData } = useReportByCategory({ ...prior, gruppe, direction, stability }, vorjahr);

  const navigate = useNavigate();
  // B4 — Klick auf Kategorie/Haendler oeffnet die gefilterte Drill-Liste; sie erbt EXAKT
  // den aktuellen Zeitraum + Verwendungsart, damit ihre Summe auf die angeklickte Zahl passt.
  const openDrill = (params: Record<string, string>) => {
    const qs = new URLSearchParams({ from: range.from, to: range.to });
    if (gruppe !== "all") qs.set("gruppe", gruppe);
    // Migration 0010: Achsen-Filter an die Drill-Liste weitergeben, damit Σ stimmt.
    if (direction !== "all") qs.set("direction", direction);
    if (stability !== "all") qs.set("stability", stability);
    for (const [k, v] of Object.entries(params)) qs.set(k, v);
    navigate(`/auswertungen/liste?${qs.toString()}`);
  };

  const cats = data?.byCategory ?? [];
  const pieData = useMemo(
    () => cats.map((c, i) => ({ ...c, fill: c.colorHex ?? PALETTE[i % PALETTE.length] ?? "#0A84FF" })),
    [cats],
  );

  const delta = vorjahr && data && priorData ? data.total - priorData.total : null;

  // Direction-spezifische Texte: bei „Ein- & Ausgaben" neutral, sonst auf die gewaehlte Richtung
  // zuschneiden — sonst sagt die Seite „Ausgaben nach Kategorie" auch wenn nur Einnahmen gefiltert sind.
  const dirLabel = direction === "expense" ? "Ausgaben" : direction === "income" ? "Einnahmen" : "Belege";

  return (
    <div className="px-5 py-6 md:px-10 md:py-8 max-w-5xl mx-auto animate-fade-in">
      <header className="mb-5">
        <h1 className="text-[28px] md:text-[34px] font-semibold tracking-tight">Auswertungen</h1>
        <p className="text-[15px] text-ios-muted mt-1">{dirLabel} nach Kategorie · {range.from} – {range.to}</p>
      </header>

      {/* Filterleiste */}
      <div className="ios-card p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-ios bg-ios-bg/60 p-0.5">
          {(Object.keys(PRESET_LABEL) as Preset[]).map((p) => (
            <button
              key={p}
              onClick={() => setPreset(p)}
              className={`text-[13px] px-3 py-1 rounded-[7px] transition ${preset === p ? "bg-white shadow-sm font-medium" : "text-ios-muted"}`}
            >
              {PRESET_LABEL[p]}
            </button>
          ))}
        </div>
        {preset !== "free" && (
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setOffset((o) => o - 1)}
              className="w-7 h-7 inline-flex items-center justify-center rounded-ios text-ios-muted hover:bg-ios-bg/60"
              aria-label="Vorherige Periode"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <span className="text-[13px] font-medium tabular-nums min-w-[8.5rem] text-center">{periodLabel(preset, range)}</span>
            <button
              type="button"
              onClick={() => setOffset((o) => Math.min(0, o + 1))}
              disabled={offset >= 0}
              className="w-7 h-7 inline-flex items-center justify-center rounded-ios text-ios-muted hover:bg-ios-bg/60 disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Nächste Periode"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        )}
        {preset === "free" && (
          <span className="flex items-center gap-1.5 text-[13px]">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="ios-input py-1" />
            <span className="text-ios-muted">–</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="ios-input py-1" />
          </span>
        )}
        <select value={gruppe} onChange={(e) => setGruppe(e.target.value)} className="ios-input py-1 text-[13px]" aria-label="Gruppe der Buchungsart">
          {GRUPPEN_FILTER.map((u) => (
            <option key={u} value={u}>{u === "all" ? "Alle Gruppen" : u}</option>
          ))}
        </select>
        {/* Migration 0010: Direction (Einnahme/Ausgabe) + Stability (variabel/fix). */}
        <select
          value={direction}
          onChange={(e) => setDirection(e.target.value as "all" | "expense" | "income")}
          className="ios-input py-1 text-[13px]"
          title="Direction"
        >
          <option value="all">Ein- &amp; Ausgaben</option>
          <option value="expense">Nur Ausgaben</option>
          <option value="income">Nur Einnahmen</option>
        </select>
        <select
          value={stability}
          onChange={(e) => setStability(e.target.value as "all" | "fixed" | "variable" | "none")}
          className="ios-input py-1 text-[13px]"
          title="Kostenart"
        >
          <option value="all">Kostenart egal</option>
          <option value="variable">Nur variabel</option>
          <option value="fixed">Nur fix</option>
          <option value="none">⚠️ ohne Kostenart-Standard</option>
        </select>
        <label className="flex items-center gap-1.5 text-[13px] text-ios-ink cursor-pointer">
          <input type="checkbox" checked={vorjahr} onChange={(e) => setVorjahr(e.target.checked)} className="accent-ios-blue" />
          Vorjahresvergleich
        </label>
      </div>

      {isLoading ? (
        <div className="ios-card p-10 text-center text-ios-muted">Lade…</div>
      ) : cats.length === 0 ? (
        <div className="ios-card p-10 text-center text-ios-muted">Keine {dirLabel} in diesem Zeitraum.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* S3: Summe je Gruppe der Buchungsart, mit „davon nur Vorschlag“ (unbestätigte Buchungsarten). Klick → Positionsliste. */}
          {data?.byGruppe && data.byGruppe.length > 0 && (
            <div className="ios-card p-5 lg:col-span-2">
              <h2 className="text-[15px] font-semibold mb-2">Nach Gruppe (Buchungsart)</h2>
              <ul className="divide-y divide-ios-line/40">
                {data.byGruppe.map((g) => (
                  <li key={g.gruppe} className="py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <button type="button" className="flex-1 min-w-[10rem] text-left text-[14px] text-ios-blue hover:underline"
                      onClick={() => openDrill({ gruppe: g.gruppe })}>
                      {g.gruppe}
                    </button>
                    <span className="tabular-nums text-[14px]"><SignedAmount value={g.total} /></span>
                    {Math.abs(g.davonVorschlag) > 0.005 && (
                      <button type="button" className="basis-full text-left text-[12px] text-ios-blue hover:underline"
                        title="Diese Beträge beruhen auf Vorschlägen (Standard der Kategorie), die noch niemand bestätigt hat."
                        onClick={() => openDrill({ gruppe: g.gruppe, nurUnbestaetigt: "1" })}>
                        💡 davon nur Vorschlag: {formatCurrency(Math.abs(g.davonVorschlag))} — ansehen
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Donut + Summe */}
          <div className="ios-card p-5">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-[15px] font-semibold">Nach Kategorie</h2>
              <span className="text-[13px] text-ios-muted">{data!.receiptCount} Belege</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="total" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {pieData.map((d) => <Cell key={d.categoryId ?? "none"} fill={d.fill} />)}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Migration 0010/0011: Σ-Split — Einnahmen / Ausgaben / Neutral / Saldo.
                Farbe blau=positiv, rot=negativ, grau=neutral (Rot-Grün-Farbschwäche-konform,
                siehe [[feedback_signed_amount_color_display]]). Vorzeichen werden NICHT angezeigt. */}
            <div className="text-center mt-1">
              {data!.totals ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-1">
                  <div>
                    <div className="text-[15px] font-semibold tabular-nums text-ios-blue">
                      {formatCurrency(data!.totals.income)}
                    </div>
                    <div className="text-[11px] text-ios-muted">Einnahmen</div>
                  </div>
                  <div>
                    <div className="text-[15px] font-semibold tabular-nums text-ios-red">
                      {formatCurrency(data!.totals.expense)}
                    </div>
                    <div className="text-[11px] text-ios-muted">Ausgaben</div>
                  </div>
                  <div>
                    <div className="text-[15px] font-semibold tabular-nums text-ios-ink">
                      <span aria-hidden="true">↔ </span>{formatCurrency(data!.totals.neutral ?? 0)}
                    </div>
                    <div className="text-[11px] text-ios-muted">Neutral</div>
                  </div>
                  <div>
                    <div className={`text-[15px] font-semibold tabular-nums ${data!.totals.net === 0 ? "text-ios-ink" : data!.totals.net < 0 ? "text-ios-blue" : "text-ios-red"}`}>
                      {formatCurrency(Math.abs(data!.totals.net))}
                    </div>
                    <div className="text-[11px] text-ios-muted">Saldo</div>
                  </div>
                </div>
              ) : (
                <>
                  <SignedAmount value={data!.total} className="text-[26px] font-semibold" />
                  <div className="text-[13px] text-ios-muted">Gesamt (effektiv)</div>
                </>
              )}
              {delta !== null && (
                // delta>0 = mehr Ausgaben/weniger Einnahmen → rot. delta<0 → blau (positiv für User).
                <div className={`text-[13px] mt-1 ${delta === 0 ? "text-ios-muted" : delta > 0 ? "text-ios-red" : "text-ios-blue"}`}>
                  {delta >= 0 ? "+" : "−"}{formatCurrency(Math.abs(delta))} ggü. Vorjahr ({formatCurrency(priorData!.total)})
                </div>
              )}
            </div>
          </div>

          {/* Kategorie-Tabelle */}
          <div className="ios-card p-5">
            <h2 className="text-[15px] font-semibold mb-3">Aufschlüsselung</h2>
            <table className="w-full text-[14px]">
              <tbody>
                {pieData.map((c: CategoryBucket & { fill: string }) => (
                  <tr
                    key={c.categoryId ?? "none"}
                    onClick={() => openDrill({ categoryId: c.categoryId ?? "none", name: c.name })}
                    className="border-b border-ios-line/40 last:border-0 cursor-pointer hover:bg-ios-bg/40 transition"
                    title={`Positionen in „${c.name}" anzeigen`}
                  >
                    <td className="py-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm mr-2 align-middle" style={{ backgroundColor: c.fill }} />
                      {c.name}
                    </td>
                    <td className="py-1.5 text-right text-ios-muted tabular-nums">{c.itemCount}×</td>
                    <td className="py-1.5 text-right"><SignedAmount value={c.total} className="font-medium" /></td>
                    <td className="py-1.5 text-right text-ios-muted tabular-nums w-12">{data!.total > 0 ? Math.round((c.total / data!.total) * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Top-Vendoren */}
          {data!.topVendors.length > 0 && (
            <div className="ios-card p-5 lg:col-span-2">
              <h2 className="text-[15px] font-semibold mb-3">Top-Händler</h2>
              <ul className="divide-y divide-ios-line/40">
                {data!.topVendors.map((v) => (
                  <li key={v.vendor}>
                    <button
                      type="button"
                      onClick={() => openDrill({ vendor: v.vendor, name: v.vendor })}
                      className="w-full py-1.5 flex items-center justify-between text-[14px] text-left cursor-pointer hover:bg-ios-bg/40 transition rounded-ios -mx-2 px-2"
                      title={`Belege von „${v.vendor}" anzeigen`}
                    >
                      <span>{v.vendor} <span className="text-ios-muted text-[12px]">· {v.count}×</span></span>
                      <SignedAmount value={v.total} className="font-medium" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Track G / Variante 1 (2026-06-05): Der „Aus Belegen filtern"-Tree wurde aus der
// Auswertungen-Page entfernt und lebt jetzt als zweite Sicht im Archiv (Tab „Nach
// Kategorie" neben „Nach Zeit"). Begründung: Auswertungen ist eine Σ-Aggregations-
// Sicht (Saldo/Cat-Tabelle/Top-Vendors), das Archiv ist die Beleg-Drill-Sicht und
// versammelt damit beide Achsen — zeitliche Hierarchie + inhaltliche Hierarchie —
// über genau denselben Belegset (reviewedAt IS NOT NULL).
